/**
 * The ceiling lights. This is the atmosphere, so it gets its own module.
 *
 * Every LightSpot always gets a real fixture in the world: the housing is baked
 * into the chunk geometry and the emissive panel is either baked in too (steady
 * and dead fixtures) or given its own mesh so it can be driven (flickering
 * ones). Only the `maxDynamicLights` fixtures nearest the player are promoted
 * to actual THREE.PointLights, and that set is re-picked as the player moves.
 *
 * `levelAt` is the analytic version of the same thing: the survival system and
 * every entity ask for it every frame, so it never touches the scene graph.
 *
 * ## The drain
 *
 * Some entities pull the room down around themselves (`render/lightdrain.ts`
 * says which and why). That lands here rather than anywhere else because the
 * fixtures are the only thing that can carry it: a fixture forty metres away
 * has no `PointLight` at all — it is a bright dot of emissive panel in the fog —
 * so putting *that* out is the entire effect at the range where it has to work.
 *
 * Which means steady fixtures need to be dimmable, and their panels are baked
 * into the chunk mesh. So a small pool of override panels is drawn over the
 * baked ones and driven exactly the way the flickering panels are. Slots go to
 * the drained fixtures *nearest the player*, which is the cheap ordering and
 * also the right one — the near edge of the dark is the edge the player is
 * looking at, and the far side of it is past the fog anyway.
 */

import * as THREE from 'three';
import { TUNING } from '../core/config';
import { clamp, clamp01, damp, hash1, lerp, noise1, smoothstep } from '../core/math';
import type { EntityState, GameContext, GeneratedLevel, Vec2 } from '../core/types';
import type { MaterialSet } from './api';
import { makeLightDrainField } from './lightdrain';
import { fixturePanelY, fixtureRotation, type LevelGeometry } from './meshbuilder';

/** How long a failing tube holds one behaviour before rolling for the next. */
const SLOT_SECONDS = 2.35;
/** Seconds between re-picking which fixtures own a real light. */
const RESELECT_INTERVAL = 0.22;
/** Metres of player movement that forces an early re-pick. */
const RESELECT_MOVE = 2.0;
/** Metres. A buzz-pop is only worth hearing if the fixture is this close. */
const CUE_RANGE = 15;
/** Seconds of silence between buzz-pops anywhere on the level. */
const CUE_COOLDOWN = 11;
/** Seconds before the same fixture may pop again. */
const CUE_FIXTURE_COOLDOWN = 55;

/**
 * Drain at which a healthy tube starts behaving like a failing one, and the
 * drain at which it is fully into that behaviour. Below the first it is just
 * sagging; a hard cut at 0 would read as a dimmer knob.
 */
const DRAIN_FLICKER_IN = 0.1;
const DRAIN_FLICKER_FULL = 0.55;
/** Below this the drain is finished and the fixture leaves the active set. */
const DRAIN_EPSILON = 0.002;
/** Above this a fixture counts as settled and gives its override panel back. */
const LIT_SETTLED = 0.985;

/**
 * An override panel is exactly coplanar with the baked one it hides, and wins
 * the depth test on `polygonOffset` rather than on a physical gap.
 *
 * A gap was the obvious approach and it does not work. The depth buffer
 * resolves about 5 mm at the 68 m cull with this near plane, so the offset
 * would have to be centimetres — and a panel hanging centimetres below its
 * housing shows a crescent of the bright baked one at any grazing angle, which
 * is precisely the range this effect has to work at. `polygonOffset` is in
 * depth-buffer units, so it scales with distance on its own and the two quads
 * stay perfectly aligned.
 *
 * The slight oversize is belt-and-braces against edge cracks; at 2% it is still
 * well inside the housing's `FIXTURE.housingMargin` overhang.
 */
const OVERRIDE_POLYGON_FACTOR = -2;
const OVERRIDE_POLYGON_UNITS = -4;
const OVERRIDE_SCALE = 1.02;

const KIND_ON = 0;
const KIND_FLICKER = 1;
const KIND_DEAD = 2;

/**
 * One failing tube's target brightness.
 *
 * A sine wave reads as a stage effect. A real dying fluorescent sits in one
 * behaviour for a couple of seconds at a time — dead, re-striking, or nearly
 * fine — and consecutive dead slots stack into the long silences that make the
 * burst afterwards land.
 */
function flickerTarget(t: number, seed: number): number {
  const slot = Math.floor(t / SLOT_SECONDS);
  const kind = hash1(slot, seed);
  if (kind < 0.36) return 0;
  if (kind < 0.63) {
    // A stutter, not a strobe. This used to run at 8-25 Hz with a 44% duty —
    // a square wave in the flicker-fusion band, which is unpleasant on a
    // monitor and genuinely unsafe in a headset filling your field of view.
    // A real failing ballast buzzes at a few hertz and is lit more often than
    // it is dark, so: 2.5-6 Hz, and dark about a quarter of the time.
    const rate = 2.5 + hash1(slot, seed ^ 0x51ed) * 3.5;
    return hash1(Math.floor(t * rate), seed ^ 0x9e37) < 0.26 ? 0 : 1;
  }
  const dropout = hash1(Math.floor(t * 5.5), seed ^ 0x2f1c) < 0.09 ? 0 : 1;
  return dropout * (0.8 + 0.2 * noise1(t * 3.3, seed));
}

/**
 * What a healthy fixture does while something is pulling the light out of it.
 *
 * Deliberately continuous: two slow sine-ish terms at incommensurate rates, so
 * it wanders rather than repeating, with a floor under it. No hard edges, no
 * binary states, nothing in the flicker-fusion band. The *drain* is what takes
 * the room dark; this only makes the dying unsteady enough to notice.
 */
function drainSag(t: number, seed: number): number {
  const wander = noise1(t * 0.55, seed) * 0.6 + noise1(t * 1.7, seed ^ 0x1d7b) * 0.4;
  // 0.45..1: it never gutters below half on its own account.
  return 0.45 + 0.55 * wander;
}

export interface LightRig {
  /**
   * 0..1 dimming of the fixtures around the player, handed to the material set
   * so damp sheen and emissives can respond to the room going dark.
   */
  readonly localFlicker: number;
  /**
   * 0..1 light drain where the player is standing, this frame. Drives the
   * things that are not fixtures — the level's flat ambient, the torch, the
   * post chain — so they agree with what the ceiling is doing.
   */
  readonly localDrain: number;
  /** True when this level runs the drain effect at all. See `levelScale`. */
  readonly drainEnabled: boolean;
  /** Hand over this frame's entity list before `update`. Allocates nothing. */
  syncDrain(entities: readonly EntityState[]): void;
  update(dt: number, time: number, playerX: number, playerZ: number, ctx: GameContext): void;
  /** Fixture-driven illumination at a world point, flicker included. Cheap. */
  levelAt(wx: number, wz: number): number;
  /** 0..1 drain at a world point. `levelAt` already has it folded in. */
  drainAt(wx: number, wz: number): number;
  dispose(): void;
}

export function makeLightRig(
  level: GeneratedLevel,
  materials: MaterialSet,
  geometry: LevelGeometry,
  scene: THREE.Scene,
  maxDynamic: number
): LightRig {
  const { grid, spec } = level;
  const spots = level.lights;
  const n = spots.length;

  const px = new Float32Array(n);
  const pz = new Float32Array(n);
  const kind = new Uint8Array(n);
  const phase = new Float32Array(n);
  const lit = new Float32Array(n);
  /**
   * What `lit` would be with no drain applied.
   *
   * `levelAt` sums fixtures and clamps at 1, and the Lobby sums to about eight
   * under a working troffer — so folding the drain into `lit` alone moves that
   * sum from 8 to 0.9 and `levelAt` still answers "fully lit" while the screen
   * has gone dark. The drain therefore comes off *after* the clamp, and the sum
   * itself is built from the undrained value so the two are not multiplied
   * together in the dim spots between fixtures where sum < 1.
   */
  const litBase = new Float32Array(n);
  const darkFor = new Float32Array(n);
  const cueAfter = new Float32Array(n);
  /** Live drain per fixture, damped. `drainTarget` is transient — see `update`. */
  const drain = new Float32Array(n);
  const drainTarget = new Float32Array(n);
  /** Wall-clock time before which a fixture will not start coming back. */
  const holdUntil = new Float32Array(n);
  const panelMats: Array<THREE.Material | null> = new Array<THREE.Material | null>(n).fill(null);
  const panelMeshes: Array<THREE.Mesh | null> = new Array<THREE.Mesh | null>(n).fill(null);

  const panelY = fixturePanelY(spec.ceilingHeight);
  const source = materials.lightPanel as THREE.Material & { emissiveIntensity?: number; color?: THREE.Color };
  const baseEmissive = typeof source.emissiveIntensity === 'number' ? source.emissiveIntensity : 1;
  const baseColor = source.color ? source.color.clone() : null;

  for (let i = 0; i < n; i++) {
    const spot = spots[i]!;
    px[i] = spot.pos.x;
    pz[i] = spot.pos.z;
    phase[i] = spot.phase;
    kind[i] = spot.state === 'dead' ? KIND_DEAD : spot.state === 'flicker' ? KIND_FLICKER : KIND_ON;
    lit[i] = kind[i] === KIND_DEAD ? 0 : 1;
    litBase[i] = lit[i]!;

    if (kind[i] !== KIND_FLICKER) continue;
    const mat = source.clone();
    const mesh = new THREE.Mesh(geometry.panelGeometry, mat);
    // Rz first, then Rx: the in-plane quarter turn happens before the tip-down.
    mesh.rotation.set(Math.PI / 2, 0, fixtureRotation(grid, spot, level.seed));
    mesh.position.set(spot.pos.x, panelY, spot.pos.z);
    mesh.matrixAutoUpdate = false;
    mesh.updateMatrix();
    panelMats[i] = mat;
    panelMeshes[i] = mesh;
    // Parented into the chunk so chunk culling takes the panel with it.
    (geometry.chunkAt(spot.pos.x, spot.pos.z)?.group ?? scene).add(mesh);
  }

  /* Dynamic light pool ------------------------------------------------- */

  const reach = clamp(Math.max(spec.ceilingHeight * 3.4, grid.cellSize * 3.6), 9, 18);
  const reach2 = reach * reach;
  // Candela, chosen so the floor directly under a fixture lands near 1.0
  // whatever the ceiling height is.
  const baseIntensity = Math.min(32, spec.ceilingHeight * spec.ceilingHeight * TUNING.render.fixtureIntensityScale);
  const slots = Math.max(1, Math.floor(maxDynamic));
  const pool: THREE.PointLight[] = [];
  const poolFixture = new Int32Array(slots).fill(-1);
  const candIdx = new Int32Array(slots);
  const candD2 = new Float32Array(slots);
  const lightY = panelY - 0.06;

  for (let i = 0; i < slots; i++) {
    const l = new THREE.PointLight(spec.palette.lightTint, 0, reach, 2);
    l.castShadow = false;
    // Kept in the scene at zero intensity rather than hidden: changing the
    // number of visible lights forces every material to recompile its shader.
    l.position.set(0, lightY, 0);
    scene.add(l);
    pool.push(l);
  }

  function reselect(x: number, z: number): void {
    let count = 0;
    for (let i = 0; i < n; i++) {
      if (kind[i] === KIND_DEAD) continue;
      const dx = x - px[i]!;
      const dz = z - pz[i]!;
      const d2 = dx * dx + dz * dz;
      if (d2 > reach2) continue;
      if (count >= slots && d2 >= candD2[slots - 1]!) continue;
      let j = count < slots ? count++ : slots - 1;
      while (j > 0 && candD2[j - 1]! > d2) {
        candD2[j] = candD2[j - 1]!;
        candIdx[j] = candIdx[j - 1]!;
        j--;
      }
      candD2[j] = d2;
      candIdx[j] = i;
    }
    for (let s = 0; s < slots; s++) {
      const idx = s < count ? candIdx[s]! : -1;
      poolFixture[s] = idx;
      if (idx >= 0) pool[s]!.position.set(px[idx]!, lightY, pz[idx]!);
    }
  }

  /* Spatial buckets for levelAt ---------------------------------------- */

  const [infMin, infMax] = TUNING.render.lightInfluenceRadius;
  const influence = clamp(Math.max(spec.ceilingHeight * 3.6, grid.cellSize * 3.8), infMin, infMax);
  const influence2 = influence * influence;
  const bw = Math.max(1, Math.ceil((grid.w * grid.cellSize) / influence));
  const bh = Math.max(1, Math.ceil((grid.h * grid.cellSize) / influence));
  const bucketStart = new Int32Array(bw * bh + 1);
  const bucketItems = new Int32Array(n);
  {
    const counts = new Int32Array(bw * bh);
    const of = new Int32Array(n);
    for (let i = 0; i < n; i++) {
      const bx = clamp(Math.floor(px[i]! / influence), 0, bw - 1) | 0;
      const bz = clamp(Math.floor(pz[i]! / influence), 0, bh - 1) | 0;
      of[i] = bz * bw + bx;
      counts[of[i]!]++;
    }
    let acc = 0;
    for (let b = 0; b < bw * bh; b++) {
      bucketStart[b] = acc;
      acc += counts[b]!;
    }
    bucketStart[bw * bh] = acc;
    const cursor = Int32Array.from(bucketStart.subarray(0, bw * bh));
    for (let i = 0; i < n; i++) bucketItems[cursor[of[i]!]++] = i;
  }

  /* Light drain --------------------------------------------------------- */

  const drainField = makeLightDrainField(spec);
  const tune = TUNING.render;
  const [holdMin, holdMax] = tune.lightRecoverHold;

  /**
   * Mark every fixture inside a drainer's reach with the drain it should be
   * heading for.
   *
   * This walks the buckets `levelAt` already built, once per *drainer* rather
   * than once per fixture — which is what stops the obvious N×M sweep. A level
   * has hundreds of fixtures and at most `entityBudget` drainers, and a 34 m
   * box over 11 m buckets is a few dozen fixtures each.
   *
   * `drainTarget` is consumed and cleared by the envelope loop below, so
   * nothing has to remember which fixtures were marked last frame.
   */
  function markDrainTargets(): void {
    for (let s = 0; s < drainField.count; s++) {
      const cx = drainField.sx[s]!;
      const cz = drainField.sz[s]!;
      const r = drainField.sr[s]!;
      const r2 = r * r;
      const amount = drainField.sa[s]!;
      const bx0 = clamp(Math.floor((cx - r) / influence), 0, bw - 1) | 0;
      const bx1 = clamp(Math.floor((cx + r) / influence), 0, bw - 1) | 0;
      const bz0 = clamp(Math.floor((cz - r) / influence), 0, bh - 1) | 0;
      const bz1 = clamp(Math.floor((cz + r) / influence), 0, bh - 1) | 0;
      for (let zz = bz0; zz <= bz1; zz++) {
        for (let xx = bx0; xx <= bx1; xx++) {
          const b = zz * bw + xx;
          const end = bucketStart[b + 1]!;
          for (let k = bucketStart[b]!; k < end; k++) {
            const i = bucketItems[k]!;
            // A dead tube has nothing left to take, and marking it would keep
            // it in the envelope loop forever for no visible result.
            if (kind[i] === KIND_DEAD) continue;
            const dx = cx - px[i]!;
            const dz = cz - pz[i]!;
            const d2 = dx * dx + dz * dz;
            if (d2 >= r2) continue;
            const t = 1 - Math.sqrt(d2) / r;
            const v = amount * t * t * (3 - 2 * t);
            if (v > drainTarget[i]!) drainTarget[i] = v;
          }
        }
      }
    }
  }

  /* Override panels ----------------------------------------------------- */

  /**
   * Steady fixtures have their emissive panel baked into the chunk mesh, so the
   * only way to put one out is to draw over it. Slots are handed to the drained
   * fixtures *nearest the player*, which is both the cheap ordering and the
   * right one: those are the ones on screen.
   */
  const overrideSlots = drainField.scale > 0 ? Math.min(32, slots * 2 + 8) : 0;
  const overrideMeshes: Array<THREE.Mesh | null> = new Array<THREE.Mesh | null>(overrideSlots).fill(null);
  const overrideMats: Array<THREE.Material | null> = new Array<THREE.Material | null>(overrideSlots).fill(null);
  const overrideFixture = new Int32Array(overrideSlots).fill(-1);
  /** This frame's nearest-first candidate list, refilled by the envelope loop. */
  const overIdx = new Int32Array(overrideSlots);
  const overD2 = new Float32Array(overrideSlots);
  let overCount = 0;
  // Past the cull boundary the chunk is not drawn, so neither is the fixture.
  const overrideRange2 = tune.cullDistance * tune.cullDistance;

  /** Nearest-first insertion into the candidate list. Mirrors `reselect`. */
  function offerOverride(i: number, d2: number): void {
    if (overrideSlots === 0 || d2 > overrideRange2) return;
    if (overCount >= overrideSlots && d2 >= overD2[overrideSlots - 1]!) return;
    let j = overCount < overrideSlots ? overCount++ : overrideSlots - 1;
    while (j > 0 && overD2[j - 1]! > d2) {
      overD2[j] = overD2[j - 1]!;
      overIdx[j] = overIdx[j - 1]!;
      j--;
    }
    overD2[j] = d2;
    overIdx[j] = i;
  }

  /** Build the mesh for a slot the first time it is needed, never before. */
  function ensureOverride(s: number): THREE.Mesh {
    const existing = overrideMeshes[s];
    if (existing) return existing;
    const mat = source.clone();
    mat.polygonOffset = true;
    mat.polygonOffsetFactor = OVERRIDE_POLYGON_FACTOR;
    mat.polygonOffsetUnits = OVERRIDE_POLYGON_UNITS;
    const mesh = new THREE.Mesh(geometry.panelGeometry, mat);
    mesh.scale.set(OVERRIDE_SCALE, OVERRIDE_SCALE, 1);
    mesh.matrixAutoUpdate = false;
    overrideMats[s] = mat;
    overrideMeshes[s] = mesh;
    return mesh;
  }

  /** Move slot `s` onto fixture `idx`, or park it if `idx` is -1. */
  function bindOverride(s: number, idx: number): void {
    if (overrideFixture[s] === idx) return;
    overrideFixture[s] = idx;
    const mesh = overrideMeshes[s];
    if (idx < 0) {
      if (mesh) {
        mesh.visible = false;
        mesh.parent?.remove(mesh);
      }
      return;
    }
    const m = ensureOverride(s);
    const spot = spots[idx]!;
    m.rotation.set(Math.PI / 2, 0, fixtureRotation(grid, spot, level.seed));
    m.position.set(px[idx]!, panelY, pz[idx]!);
    m.updateMatrix();
    m.visible = true;
    // Parented into the chunk so chunk culling takes the override with it, the
    // same way the flicker panels are.
    (geometry.chunkAt(px[idx]!, pz[idx]!)?.group ?? scene).add(m);
  }

  /* Frame state --------------------------------------------------------- */

  let sinceReselect = RESELECT_INTERVAL;
  let lastX = Number.NEGATIVE_INFINITY;
  let lastZ = 0;
  let nextCue = Number.NEGATIVE_INFINITY;
  let localFlicker = 1;
  let localDrain = 0;
  const cuePos: Vec2 = { x: 0, z: 0 };

  return {
    get localFlicker(): number {
      return localFlicker;
    },
    get localDrain(): number {
      return localDrain;
    },
    drainEnabled: drainField.scale > 0,

    syncDrain(entities: readonly EntityState[]): void {
      drainField.sync(entities);
    },

    update(dt: number, time: number, playerX: number, playerZ: number, ctx: GameContext): void {
      if (nextCue === Number.NEGATIVE_INFINITY) nextCue = time + 14;

      markDrainTargets();
      localDrain = drainField.at(playerX, playerZ);
      overCount = 0;

      for (let i = 0; i < n; i++) {
        // A dead tube has nothing to drain and nothing to recover, and it must
        // leave before the envelope below — which drives everything toward lit
        // — gets hold of it and switches the level's broken fixtures on.
        const k = kind[i]!;
        if (k === KIND_DEAD) continue;
        const flickers = k === KIND_FLICKER;
        let d = drain[i]!;
        const dTarget = drainTarget[i]!;
        // A steady tube with nothing happening to it is the common case and has
        // to cost one branch. Note the settle test: a fixture whose drain has
        // finished still has to be walked back up to full and hand its override
        // panel back, or it is stranded dim.
        if (!flickers && d <= 0 && dTarget <= 0 && lit[i]! >= LIT_SETTLED) continue;
        drainTarget[i] = 0;

        if (dTarget > d) {
          d = damp(d, dTarget, tune.lightDrainRate, dt);
          // The hold clock only starts once the dark stops deepening, so a
          // fixture the entity is still standing under never begins to recover.
          holdUntil[i] = time + lerp(holdMin, holdMax, hash1(i, 0x5bd1));
        } else if (dTarget < d && time >= holdUntil[i]!) {
          // Recovery is slower than the drain and each fixture recovers at its
          // own rate, so the room comes back raggedly instead of switching on.
          d = damp(d, dTarget, tune.lightRecoverRate * (0.45 + 1.5 * hash1(i, 0x2c9f)), dt);
        }
        if (d < DRAIN_EPSILON) d = 0;
        drain[i] = d;

        const prev = lit[i]!;
        // Reuse the failing-tube envelope rather than writing a second one: a
        // healthy tube being pulled down should behave like a broken one, and
        // `w` is how far into that behaviour the drain has pushed it.
        const w = flickers ? 1 : smoothstep(DRAIN_FLICKER_IN, DRAIN_FLICKER_FULL, d);
        // A healthy fixture being pulled down does **not** borrow the broken-tube
        // envelope. That was the strobe: a drainer walking in put every fixture
        // inside 34 m onto a hard on/off envelope at once, so an entire wing of
        // the floor blinked together. One failing tube in a corridor is
        // atmosphere; forty of them is a light show, and in a headset it is a
        // hazard. A drained fixture sags and gutters instead — continuous,
        // slow, and never fully off until the drain itself takes it there.
        const env = !w
          ? 1
          : flickers
            ? flickerTarget(time * 0.85 + phase[i]!, i * 0x9e3779b9)
            : drainSag(time + phase[i]!, i * 0x9e3779b9);
        const clean = flickers ? env : 1;
        let target = (1 - w + w * env) * (1 - d * tune.lightDrainDepth);
        if (target > 1) target = 1;
        // Fast strike, slower phosphor decay.
        const next = damp(prev, target, target > prev ? 60 : 15, dt);
        const settled = next > LIT_SETTLED && d <= 0 && !flickers;
        lit[i] = settled ? 1 : next;
        litBase[i] = settled ? 1 : damp(litBase[i]!, clean, clean > litBase[i]! ? 60 : 15, dt);

        if (overrideSlots > 0 && !flickers && next < LIT_SETTLED) {
          const ox = playerX - px[i]!;
          const oz = playerZ - pz[i]!;
          offerOverride(i, ox * ox + oz * oz);
        }

        if (next < 0.12) {
          darkFor[i] = darkFor[i]! + dt;
        } else if (next > 0.5) {
          // A tube coming back after a real silence is the sound worth having.
          if (
            time >= nextCue &&
            prev <= 0.5 &&
            darkFor[i]! > 1.1 &&
            time >= cueAfter[i]! &&
            hash1(Math.floor(time * 17), i) < 0.55
          ) {
            const dx = playerX - px[i]!;
            const dz = playerZ - pz[i]!;
            if (dx * dx + dz * dz < CUE_RANGE * CUE_RANGE) {
              cuePos.x = px[i]!;
              cuePos.z = pz[i]!;
              ctx.audio.play('light-buzz-pop', { pos: cuePos });
              nextCue = time + CUE_COOLDOWN + hash1(Math.floor(time), 0x1f) * 14;
              cueAfter[i] = time + CUE_FIXTURE_COOLDOWN;
            }
          }
          darkFor[i] = 0;
        }

        const mat = panelMats[i];
        if (mat) {
          const m = mat as THREE.Material & { emissiveIntensity?: number; color?: THREE.Color };
          if (typeof m.emissiveIntensity === 'number') m.emissiveIntensity = baseEmissive * next;
          if (m.color && baseColor) m.color.copy(baseColor).multiplyScalar(0.22 + 0.78 * next);
        }
      }

      // Drive the override panels. `overIdx` is already nearest-first, so a
      // slot usually keeps its fixture from frame to frame and `bindOverride`
      // costs nothing; only the boundary of the set actually moves.
      for (let s = 0; s < overrideSlots; s++) {
        const idx = s < overCount ? overIdx[s]! : -1;
        bindOverride(s, idx);
        if (idx < 0) continue;
        const m = overrideMats[s] as (THREE.Material & { emissiveIntensity?: number; color?: THREE.Color }) | null;
        if (!m) continue;
        const l = lit[idx]!;
        if (typeof m.emissiveIntensity === 'number') m.emissiveIntensity = baseEmissive * l;
        if (m.color && baseColor) m.color.copy(baseColor).multiplyScalar(0.22 + 0.78 * l);
      }

      const moved = (playerX - lastX) * (playerX - lastX) + (playerZ - lastZ) * (playerZ - lastZ);
      sinceReselect += dt;
      if (sinceReselect >= RESELECT_INTERVAL || moved > RESELECT_MOVE * RESELECT_MOVE) {
        sinceReselect = 0;
        lastX = playerX;
        lastZ = playerZ;
        reselect(playerX, playerZ);
      }

      // Mains wobble. All the tubes share a supply, so they share the sag.
      const hum = 1 - 0.03 * noise1(time * 6.1, 0x7a11);
      for (let s = 0; s < slots; s++) {
        const idx = poolFixture[s]!;
        pool[s]!.intensity = idx < 0 ? 0 : baseIntensity * lit[idx]! * hum;
      }

      const nearest = poolFixture[0]!;
      if (nearest < 0) {
        localFlicker = 1;
      } else {
        const dx = playerX - px[nearest]!;
        const dz = playerZ - pz[nearest]!;
        const w = clamp01(1 - Math.sqrt(dx * dx + dz * dz) / reach);
        localFlicker = 1 - (1 - lit[nearest]!) * w;
      }
    },

    levelAt(wx: number, wz: number): number {
      const bx = clamp(Math.floor(wx / influence), 0, bw - 1) | 0;
      const bz = clamp(Math.floor(wz / influence), 0, bh - 1) | 0;
      let sum = 0;
      let sumLit = 0;
      for (let oz = -1; oz <= 1; oz++) {
        const zz = bz + oz;
        if (zz < 0 || zz >= bh) continue;
        for (let ox = -1; ox <= 1; ox++) {
          const xx = bx + ox;
          if (xx < 0 || xx >= bw) continue;
          const b = zz * bw + xx;
          const end = bucketStart[b + 1]!;
          for (let k = bucketStart[b]!; k < end; k++) {
            const i = bucketItems[k]!;
            const l = litBase[i]!;
            if (l <= 0.002) continue;
            const dx = wx - px[i]!;
            const dz = wz - pz[i]!;
            const d2 = dx * dx + dz * dz;
            if (d2 >= influence2) continue;
            const t = 1 - d2 / influence2;
            let f = t * t;
            // Two grid samples along the ray. Not shadowing, but enough that a
            // lit room does not brighten the corridor on the far side of a wall.
            if (l * f > 0.04 && (grid.isSolidAt(wx - dx * 0.5, wz - dz * 0.5) || grid.isSolidAt(wx - dx * 0.82, wz - dz * 0.82))) {
              f *= 0.25;
            }
            sum += l * f;
            sumLit += lit[i]! * f;
          }
        }
      }
      // The drain comes off after the clamp — that is what stops an over-lit
      // room (the Lobby sums to about eight under a troffer) from answering
      // "fully lit" while the screen has gone dark.
      //
      // It is measured from the fixtures themselves rather than from the
      // analytic field, because the field and the ceiling do not agree on the
      // way back up: the field snaps to zero the instant the drainer is gone
      // while the tubes are still in their stagger and their hold, up to about
      // six seconds of it. Reading the field there told every entity the player
      // was standing in a fully lit room that the player could not see across.
      // `lit` already carries the damp, the hold and the per-fixture stagger, so
      // the ratio tracks the picture exactly in both directions and costs one
      // multiply-add in a loop that was already running.
      const capped = sum > 1 ? 1 : sum;
      if (sum <= 0) return capped;
      const ratio = sumLit / sum;
      return ratio >= 1 ? capped : capped * ratio;
    },

    drainAt(wx: number, wz: number): number {
      return drainField.at(wx, wz);
    },

    dispose(): void {
      for (let s = 0; s < overrideSlots; s++) {
        const mesh = overrideMeshes[s];
        if (mesh) mesh.parent?.remove(mesh);
        overrideMats[s]?.dispose();
        overrideMeshes[s] = null;
        overrideMats[s] = null;
        overrideFixture[s] = -1;
      }
      for (const l of pool) {
        l.parent?.remove(l);
        l.dispose();
      }
      pool.length = 0;
      for (let i = 0; i < n; i++) {
        const mesh = panelMeshes[i];
        if (mesh) mesh.parent?.remove(mesh);
        panelMats[i]?.dispose();
        panelMeshes[i] = null;
        panelMats[i] = null;
      }
    },
  };
}
